// src/hrms/alarms/alarmModel.ts
import { localDateTimeString } from "./alarmSchedule";
import {
  type AlarmFollowUp,
  type AlarmHandledAction,
  type AlarmItemSnapshot,
  type AlarmSettings,
  type ItemAlarm,
} from "./alarmTypes";

/** Pure transitions of one stored alarm (ported from mobile). The store persists; the engine rings. */

let idCounter = 0;
export function newAlarmId(prefix: string) {
  idCounter += 1;
  return `${prefix}${Date.now().toString(36)}${idCounter.toString(36)}`;
}

export function defaultAlarmSettings(hasDueDate: boolean, now = Date.now()): AlarmSettings {
  return {
    alerts: hasDueDate
      ? [{ id: newAlarmId("a"), kind: "offset", minutesBefore: 0 }]
      : [{ at: localDateTimeString(now + 60 * 60_000), id: newAlarmId("a"), kind: "at" }],
    enabled: true,
    repeatEveryMinutes: 5,
    snoozeMinutes: 10,
    sound: "classic",
    vibrate: true,
  };
}

export function alarmSettingsOf(alarm: ItemAlarm): AlarmSettings {
  return {
    alerts: alarm.alerts,
    enabled: alarm.enabled,
    repeatEveryMinutes: alarm.repeatEveryMinutes,
    snoozeMinutes: alarm.snoozeMinutes,
    sound: alarm.sound,
    vibrate: alarm.vibrate,
  };
}

/**
 * Saves the form's settings. Re-arming means alerts the edit left in the past
 * are not reported as missed, and a re-enabled alarm starts clean.
 */
export function applyAlarmSettings(existing: ItemAlarm | undefined, item: AlarmItemSnapshot, settings: AlarmSettings, now: number): ItemAlarm {
  const reopened = Boolean(existing?.closed);
  return {
    ...settings,
    armedAt: now,
    closed: reopened ? undefined : existing?.closed,
    followUps: settings.enabled && !reopened ? existing?.followUps ?? [] : [],
    handled: existing?.handled ?? {},
    item,
    pendingCompletion: existing?.pendingCompletion,
  };
}

/**
 * Brings the stored copy of an item up to date with the server. A moved due
 * date re-arms the alarm (its offset alerts move with it); a finished item
 * closes it, which silences every alert still pending.
 */
export function syncAlarmItem(alarm: ItemAlarm, item: AlarmItemSnapshot, finished: false | "completed" | "cancelled", now: number): ItemAlarm {
  const dueMoved = alarm.item.dueAt !== item.dueAt;
  const unchanged = !dueMoved
    && alarm.item.title === item.title
    && (alarm.item.description ?? null) === (item.description ?? null)
    && alarm.item.priority === item.priority;
  if (finished) {
    if (alarm.closed === finished && unchanged) return alarm;
    return { ...alarm, closed: finished, item, pendingCompletion: false };
  }
  // A completion this browser has not delivered yet: the server's "open" is stale.
  if (alarm.pendingCompletion) return unchanged ? alarm : { ...alarm, item };
  // The item was reopened on the server (or on another device).
  const reopened = alarm.closed === "completed" || alarm.closed === "cancelled";
  if (unchanged && !reopened) return alarm;
  return {
    ...alarm,
    armedAt: dueMoved || reopened ? now : alarm.armedAt,
    closed: reopened ? undefined : alarm.closed,
    item,
  };
}

export function markOccurrenceHandled(alarm: ItemAlarm, occurrenceId: string, action: AlarmHandledAction, now: number): ItemAlarm {
  return { ...alarm, handled: { ...alarm.handled, [occurrenceId]: { action, at: now } } };
}

/** Snooze and "remind me again": handle the ringing occurrence and book a new one-off alert. */
export function addAlarmFollowUp(alarm: ItemAlarm, occurrenceId: string | null, reason: AlarmFollowUp["reason"], at: number, now: number): ItemAlarm {
  const handled = occurrenceId ? markOccurrenceHandled(alarm, occurrenceId, "snoozed", now) : alarm;
  // Follow-ups whose time has long passed are dead weight in storage.
  const recent = handled.followUps.filter((followUp) => followUp.at > now - 7 * 24 * 60 * 60_000);
  return {
    ...handled,
    enabled: true,
    followUps: [...recent, { at, id: newAlarmId(reason === "snooze" ? "s" : "r"), reason }],
  };
}

export function closeAlarm(alarm: ItemAlarm, reason: NonNullable<ItemAlarm["closed"]>, pendingCompletion = false): ItemAlarm {
  return { ...alarm, closed: reason, pendingCompletion };
}

export function priorityLabel(priority: string) {
  const lower = priority.toLocaleLowerCase().replace(/_/g, " ");
  return lower.charAt(0).toLocaleUpperCase() + lower.slice(1);
}
